import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppNotification } from '../models/notification.model';
import { SignalRService } from './signalr.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private static readonly STORAGE_KEY = 'kinexops_notifications';

  private notifications$ = new BehaviorSubject<AppNotification[]>([]);
  notifications = this.notifications$.asObservable();

  private jobNotificationBuffer: Record<string, any[]> = {};
  private jobNotificationTimers: Record<string, any> = {};

  private reminderInterval: any;
  private static readonly REMINDER_INTERVAL_MS = 5 * 60 * 1000;

  constructor(private signalR: SignalRService) {
    this.loadFromStorage();
    this.connectSignalR();
    this.startUnreadReminder();
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(NotificationService.STORAGE_KEY);
      if (!raw) return;

      const parsed: AppNotification[] = JSON.parse(raw);
      const revived = parsed.map(n => ({ ...n, timestamp: new Date(n.timestamp) }));
      this.notifications$.next(revived);
    } catch {
      // Corrupted or unavailable storage — start with an empty list.
    }
  }

  private saveToStorage(notifications: AppNotification[]): void {
    try {
      localStorage.setItem(
        NotificationService.STORAGE_KEY,
        JSON.stringify(notifications)
      );
    } catch {
      // Storage full or unavailable — non-fatal.
    }
  }

  private connectSignalR(): void {
    this.signalR.startConnection().then(() => {

      this.signalR.on('WorkerOnline', (data: any) => {
        this.add({
          type:    'worker_online',
          title:   'Worker Online',
          message: `${data.fullName || 'A worker'} just came online`,
          link:    '/live-map'
        });
      });

      this.signalR.on('WorkerOffline', (data: any) => {
        this.add({
          type:    'worker_offline',
          title:   'Worker Offline',
          message: `${data.fullName || 'A worker'} just went offline`,
          link:    '/live-map'
        });
      });

      this.signalR.on('WorkLogSubmitted', (data: any) => {
        this.add({
          type:    'work_log_submitted',
          title:   'Work Log Submitted',
          message: `${data.workerName || 'A worker'} submitted a log for ${data.jobTitle || 'a job'} — ${data.hoursSpent}h`,
          link:    `/jobs/${data.jobId}`
        });
      });

      this.signalR.on('JobNotification', (notif: any) => {
        const jobId = notif.jobId || 'general';

        if (!this.jobNotificationBuffer[jobId]) {
          this.jobNotificationBuffer[jobId] = [];
        }

        this.jobNotificationBuffer[jobId].push(notif);

        if (this.jobNotificationTimers[jobId]) {
          clearTimeout(this.jobNotificationTimers[jobId]);
        }

        this.jobNotificationTimers[jobId] = setTimeout(() => {

          const grouped = this.jobNotificationBuffer[jobId];
          delete this.jobNotificationBuffer[jobId];

          const count = grouped.length;

          let message = '';

          if (count === 1) {
            const n = grouped[0];
            message = n.message || `${n.workerName || 'A worker'} updated this job`;
          } else {
            const workers = grouped
              .map(x => x.workerName || 'A worker')
              .filter((v, i, a) => a.indexOf(v) === i);

            message = `${workers.join(', ')} — ${count} updates on this job`;
          }

          this.add({
            type:    'job_notification',
            title:   count > 1 ? 'Multiple Job Updates' : 'Job Update',
            message: message,
            link:    `/jobs/${jobId}`
          });

        }, 5000);
      });

      // ✅ NEW — worker has left the geofence of their checked-in job.
      this.signalR.on('WorkerOutOfGeofence', (data: any) => {
        const distanceKm = (data.distanceMeters / 1000).toFixed(1);
        this.add({
          type:    'geofence_exit',
          title:   '📍 Worker Left Job Site',
          message: `${data.workerName || 'A worker'} is ${distanceKm}km from "${data.jobTitle}" — outside the permitted radius.`,
          link:    `/jobs/${data.jobId}`
        });
      });

      // ✅ NEW — worker has returned inside the geofence. Lower-priority,
      // informational only — no toast vibration urgency needed.
      this.signalR.on('WorkerBackInGeofence', (data: any) => {
        this.add({
          type:    'geofence_return',
          title:   '✓ Worker Returned to Site',
          message: `${data.workerName || 'A worker'} is back within range of "${data.jobTitle}".`,
          link:    `/jobs/${data.jobId}`
        });
      });

    }).catch(() => {});
  }

  private startUnreadReminder(): void {
    this.reminderInterval = setInterval(() => {
      const unread = this.notifications$.getValue().filter(n => !n.read);
      if (unread.length === 0) return;

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Unread notifications',
        text: `You have ${unread.length} unread notification${unread.length > 1 ? 's' : ''} waiting.`,
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
    }, NotificationService.REMINDER_INTERVAL_MS);
  }

  private showToast(notification: AppNotification): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: this.getIcon(notification.type),
      title: notification.title,
      text: notification.message,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true
    });
  }

  private vibrate(type: string): void {
    if (!('vibrate' in navigator)) return;

    switch (type) {
      case 'worker_online':
        navigator.vibrate([100, 50, 100]);
        break;
      case 'worker_offline':
        navigator.vibrate([300]);
        break;
      case 'work_log_submitted':
        navigator.vibrate([100, 50, 100, 50, 100]);
        break;
      case 'job_notification':
        navigator.vibrate([200, 100, 200]);
        break;
      case 'geofence_exit':
        navigator.vibrate([200, 100, 200, 100, 200]);
        break;
      default:
        navigator.vibrate(100);
    }
  }

  private getIcon(type: string): 'success' | 'info' | 'warning' | 'error' {
    switch (type) {
      case 'worker_online':
        return 'success';
      case 'worker_offline':
        return 'warning';
      case 'work_log_submitted':
        return 'info';
      case 'job_notification':
        return 'info';
      case 'geofence_exit':
        return 'warning';
      case 'geofence_return':
        return 'success';
      default:
        return 'info';
    }
  }

  private add(
    partial: Omit<AppNotification, 'id' | 'timestamp' | 'read'> & { CreatedAt?: Date }
  ): void {

    const notification: AppNotification = {
      id:        crypto.randomUUID(),
      timestamp: partial.CreatedAt || new Date(),
      read:      false,
      ...partial
    };

    const current = this.notifications$.getValue();
    const updated = [notification, ...current].slice(0, 50);
    this.notifications$.next(updated);
    this.saveToStorage(updated);

    this.showToast(notification);
    this.vibrate(notification.type);
  }

  markAllRead(): void {
    const updated = this.notifications$.getValue().map(n => ({ ...n, read: true }));
    this.notifications$.next(updated);
    this.saveToStorage(updated);
  }

  markRead(id: string): void {
    const updated = this.notifications$.getValue()
      .map(n => n.id === id ? { ...n, read: true } : n);
    this.notifications$.next(updated);
    this.saveToStorage(updated);
  }

  removeOne(id: string): void {
    const updated = this.notifications$.getValue().filter(n => n.id !== id);
    this.notifications$.next(updated);
    this.saveToStorage(updated);
  }

  clearAll(): void {
    this.notifications$.next([]);
    this.saveToStorage([]);
  }

  get unreadCount(): number {
    return this.notifications$.getValue().filter(n => !n.read).length;
  }
}